import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { auth } from "../../services/firebaseAuth";
import { Alert, Button, Grid, useTheme } from "@mui/material";
import isEmail from "validator/lib/isEmail";
import { useAuthState } from "react-firebase-hooks/auth";
import CustomTextField from "../Base/CustomTextField";
import BackgroundWrapper from "../BackgroundWrapper";
import { AppInfoBanner } from "../Base/AppInfoBanner";
import { AuthPageLink } from "../Base/AuthPageLink";
import { getRedirectUrl } from "../../utils/redirectUtils";
import { sendCustomPasswordResetEmail } from "../../services/firebaseCloudFunctions";
import {
  getConfiguredTournoiKey,
  getRuntimeFrontBaseUrl,
} from "../../utils/tournoiResolution";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleReset = async () => {
    try {
      const tournamentKey = getConfiguredTournoiKey();
      await sendCustomPasswordResetEmail({
        email,
        ...(tournamentKey ? { tournamentKey } : {}),
        frontBaseUrl: getRuntimeFrontBaseUrl(),
      });
      setIsEmailSent(true);
      setErrorMsg("");
    } catch {
      setErrorMsg("Erreur lors de l'envoi de l'e-mail. Vérifie l'adresse.");
    }
  };

  const [user, loading] = useAuthState(auth);

  useEffect(() => {
    if (loading) return;
    if (user) {
      // Rediriger vers l'URL sauvegardée ou la page d'accueil
      navigate(getRedirectUrl());
    }
  }, [user, loading, navigate]);

  const theme = useTheme();

  return (
    <BackgroundWrapper>
      <Grid
        container
        direction="column"
        rowSpacing={4}
        justifyContent="center"
        alignItems="center"
        sx={{
          maxWidth: "600px", // Largeur maximale
          width: "100%", // Prend toute la largeur disponible
          mx: "auto", // Centré horizontalement
        }}
      >
        <Grid item sx={{ width: "100%" }}>
          <AppInfoBanner>
            Pour réinitialiser votre mot de passe, entrez votre adresse e-mail
            ci-dessous. Vous recevrez un e-mail avec les instructions pour
            réinitialiser votre mot de passe.
          </AppInfoBanner>
        </Grid>
        <Grid item sx={{ width: "100%" }}>
          <CustomTextField
            fullWidth
            type="email"
            label="Email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            onClick={handleReset}
            disabled={!isEmail(email)}
            sx={{
              width: "100%",
              py: 1.5,
              textTransform: "none",
              fontWeight: 600,
              mt: 2,
              backgroundColor: theme.palette.primary.main,
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
                transform: "scale(1.05)", // Agrandissement du bouton au survol
                transition: "transform 0.3s ease-in-out", // Transition douce
              },
            }}
          >
            Envoyer un e-mail de réinitialisation
          </Button>
          {isEmailSent && (
            <Grid item>
              <Alert sx={{ mt: 2 }} severity="success">
                Un e-mail a été envoyé pour réinitialiser votre mot de passe.
              </Alert>
            </Grid>
          )}
          {errorMsg && (
            <Grid item>
              <Alert severity="error">{errorMsg}</Alert>
            </Grid>
          )}
        </Grid>

        <Grid item container justifyContent="space-between" sx={{ mt: 2 }}>
          <Grid item>
            <AuthPageLink to="/signup">Pas de compte ?</AuthPageLink>
          </Grid>
          <Grid item>
            <AuthPageLink to="/login">
              Déjà un compte ? Connectez-vous
            </AuthPageLink>
          </Grid>
        </Grid>
      </Grid>
    </BackgroundWrapper>
  );
};

export default ResetPassword;
